import React, { ChangeEvent, useEffect, useState } from 'react';
import { Amplitude, Events } from '@iwoca/frontend-tracking-library';
import { GetDirectorSearchResponse } from '@iwoca/lapi-client/edge';
import { fetchGetApiIntroducerPortalDirectorSearch } from '@iwoca/lapi-client/introducer_portal';
import { Label, Message, Select } from '@iwoca/orion';
import _ from 'lodash';
import { ButtonLink } from 'components/ButtonLink/ButtonLink';
import { useAmplitude } from 'hooks/useAmplitude';
import { useLocale } from 'hooks/useLocale';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { DirectorModal } from './DirectorModal';
import styles from './DirectorSearch.module.css';

type TProps = {
  onChange: (person?: GetDirectorSearchResponse['data'][number]) => void;
  label: string;
  companyNumber?: string;
  showCannotFindApplicantModal?: boolean;
  error?: string;
};

export const DirectorSearch = ({
  onChange,
  label,
  companyNumber,
  showCannotFindApplicantModal = true,
  error,
}: TProps) => {
  const { locale } = useLocale();
  const { trackEvent } = useAmplitude();
  const [selectedOption, setSelectedOption] = useState('');
  const [showDirectorModal, setShowDirectorModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [directorOptions, setDirectorOptions] = useState<
    GetDirectorSearchResponse['data']
  >([]);

  useEffect(() => {
    onChange();
    setSelectedOption('');
    const fetchDirectors = async () => {
      try {
        setIsLoading(true);
        const directors = await (
          await fetchGetApiIntroducerPortalDirectorSearch({
            companyNumber: companyNumber as string,
          })
        ).json();
        setDirectorOptions(directors.data);
      } catch (e) {
        setDirectorOptions([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (companyNumber) {
      fetchDirectors();
    } else {
      setDirectorOptions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyNumber]);

  const disabled = isLoading || !companyNumber;

  const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
    // Can non-null associatedPeople as they can only select
    // if the options exist
    const director = directorOptions![parseInt(e.target.value, 10)];

    trackEvent(AMPLITUDE_EVENTS.directorSelected, {
      'Director name': `${director.first_name} ${director.last_name}`,
    });

    onChange(director);
    setSelectedOption(e.target.value);
  };

  const renderOptions = () => {
    if (!directorOptions) {
      return <option disabled>No directors found</option>;
    } else
      return directorOptions?.map((person, index) => (
        <option key={index} value={index}>
          {_.upperCase(person.last_name)}, {person.first_name}{' '}
          {person.roles?.includes('director') && '(Director)'}
        </option>
      ));
  };

  return (
    <>
      <div className={styles.labelContainer}>
        <Label htmlFor="director-search">{label}</Label>
        {showCannotFindApplicantModal && (
          <ButtonLink
            type="button"
            className={styles.showDirectorModalButton}
            data-testid="manual-address-btn"
            onClick={() => {
              setShowDirectorModal(true);
              Amplitude.track(
                Events.introducerPortal.introducerPortalEventCreator({
                  event: AMPLITUDE_EVENTS.modalOpened,
                  locale,
                  payload: {
                    'Page name': document.title,
                    URL: window.location.pathname,
                    'Modal name': 'Applicant not listed',
                  },
                }),
              );
            }}
          >
            I can’t find the applicant's name on the list
          </ButtonLink>
        )}
      </div>
      <Select
        defaultValue={selectedOption}
        onChange={handleChange}
        disabled={disabled}
        id="director-search"
      >
        <option value="" disabled>
          Choose an option
        </option>
        {renderOptions()}
      </Select>
      {showDirectorModal && (
        <DirectorModal
          showModal={showDirectorModal}
          closeModal={() => setShowDirectorModal(false)}
        />
      )}

      {error && (
        <Message className={styles.error} message={error} type="error" />
      )}
    </>
  );
};
